import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { EMPTY_DISPLAY } from './emptyDisplay';
import environment from './environment';
import i18n from './i18n';
import {
  getCurrencyFormatter,
  getDateFormatter,
  getDateTimeFormatter,
  getDateTimeShortFormatter,
} from '@/utils/intlFormatters';
import { numberLocaleForLanguage } from '@/utils/localeUtils';

/** Date/time locale from the active UI language, not the hardcoded environment locale. */
function uiDateLocale(): string {
  return numberLocaleForLanguage(i18n.language || 'fi');
}

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge for deduplication
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date string from the API, treating it as UTC if no timezone is specified
 */
function parseApiDate(dateString: string): Date {
  // Fast path: already has an explicit timezone (Z or ±HH:mm)
  if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
    return new Date(dateString);
  }

  // Safari/iOS is strict about non-ISO formats. Normalize common API variants to valid ISO 8601.
  // - "YYYY-MM-DD"                -> "YYYY-MM-DDT00:00:00Z"
  // - "YYYY-MM-DD HH:mm:ss(.SSS)" -> "YYYY-MM-DDTHH:mm:ss(.SSS)Z"
  // - "YYYY-MM-DDTHH:mm:ss(.SSS)" -> "...Z"
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(`${dateString}T00:00:00Z`);
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d{1,})?)?$/.test(dateString)) {
    return new Date(dateString.replace(' ', 'T') + 'Z');
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,})?)?$/.test(dateString)) {
    return new Date(dateString + 'Z');
  }

  // Fallback: try appending Z (kept for backwards compatibility with any other API formats)
  return new Date(dateString + 'Z');
}

/**
 * Convert an API date (string/Date) to an ISO date input value (YYYY-MM-DD).
 * Returns empty string for invalid values.
 */
export function toISODateInputValue(date: Date | string | null | undefined): string {
  const d = toValidDate(date);
  return d ? d.toISOString().slice(0, 10) : '';
}

/**
 * Parse and validate date - returns null for invalid dates (avoids RangeError in formatToParts)
 */
function toValidDate(date: Date | string | null | undefined): Date | null {
  if (date == null) return null;
  const d = typeof date === 'string' ? parseApiDate(date) : date;
  return d instanceof Date && Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Format date to locale string with proper timezone handling
 * Assumes API dates are in UTC if no timezone is specified
 * Formats as dd.MM.y to match Angular frontend
 * Returns '-' for invalid dates to avoid RangeError in formatToParts
 */
export function formatDate(
  date: Date | string | null | undefined,
  locale = uiDateLocale(),
  _timezone = environment.timezone,
): string {
  const dateObj = toValidDate(date);
  if (dateObj == null) return EMPTY_DISPLAY;

  // Format to match Angular date pipe format: dd.MM.y
  const parts = getDateFormatter(locale).formatToParts(dateObj);
  const day = parts.find((p) => p.type === 'day')?.value || '';
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';

  return `${day}.${month}.${year}`;
}

/**
 * Format date and time to locale string with proper timezone handling
 * Assumes API dates are in UTC if no timezone is specified
 * Formats as dd.MM.y HH:mm:ss to match Angular frontend
 * Returns '-' for invalid dates to avoid RangeError in formatToParts
 */
export function formatDateTime(
  date: Date | string | null | undefined,
  locale = uiDateLocale(),
  _timezone = environment.timezone,
): string {
  const dateObj = toValidDate(date);
  if (dateObj == null) return EMPTY_DISPLAY;

  // Format to match Angular date pipe format: dd.MM.y HH:mm:ss
  const parts = getDateTimeFormatter(locale).formatToParts(dateObj);
  const day = parts.find((p) => p.type === 'day')?.value || '';
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  const hour = parts.find((p) => p.type === 'hour')?.value || '';
  const minute = parts.find((p) => p.type === 'minute')?.value || '';
  const second = parts.find((p) => p.type === 'second')?.value || '';

  return `${day}.${month}.${year} ${hour}:${minute}:${second}`;
}

/**
 * Format date with full month name and time
 * Assumes API dates are in UTC if no timezone is specified
 * Returns '-' for invalid dates
 */
export function formatDateTimeLong(
  date: Date | string | null | undefined,
  locale = uiDateLocale(),
  _timezone = environment.timezone,
): string {
  const dateObj = toValidDate(date);
  if (dateObj == null) return EMPTY_DISPLAY;
  return dateObj.toLocaleString(locale, {
    timeZone: _timezone,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date and time without seconds (Finnish: dd.MM.y HH.mm)
 * Returns '-' for invalid dates to avoid RangeError in formatToParts
 */
export function formatDateTimeShort(
  date: Date | string | null | undefined,
  locale = uiDateLocale(),
  _timezone = environment.timezone,
): string {
  const dateObj = toValidDate(date);
  if (dateObj == null) return EMPTY_DISPLAY;

  const parts = getDateTimeShortFormatter(locale).formatToParts(dateObj);
  const day = parts.find((p) => p.type === 'day')?.value || '';
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  const hour = parts.find((p) => p.type === 'hour')?.value || '';
  const minute = parts.find((p) => p.type === 'minute')?.value || '';

  return `${day}.${month}.${year} ${hour}${locale.startsWith('fi') ? '.' : ':'}${minute}`;
}

/** Time of day only (HH:mm); used where the date is already implied, e.g. calendar cells. */
export function formatTimeOfDay(date: Date | string | null | undefined, locale = uiDateLocale()): string {
  const dateObj = toValidDate(date);
  if (dateObj == null) return EMPTY_DISPLAY;

  const parts = getDateTimeShortFormatter(locale).formatToParts(dateObj);
  const hour = parts.find((p) => p.type === 'hour')?.value || '';
  const minute = parts.find((p) => p.type === 'minute')?.value || '';
  return hour && minute ? `${hour}:${minute}` : '-';
}

/**
 * Format currency
 */
export function formatCurrency(amount: number, _currency = 'EUR', locale = environment.locale): string {
  return getCurrencyFormatter(locale).format(amount);
}

/**
 * Check if an invitation is expired (older than 7 days)
 * @param createdAt The creation date of the invitation
 * @returns boolean indicating if the invitation is expired (true for invalid dates)
 */
export function isInvitationExpired(createdAt: Date | string | null | undefined): boolean {
  const createdDate = toValidDate(createdAt);
  if (createdDate == null) return true;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return createdDate <= sevenDaysAgo;
}
